/**
 * KMD diagnostics — stable-code registry (spec §9). Codes are frozen API:
 * AI repair loops depend on them. This registry is the single source of
 * truth for code → severity/meaning; the machine-readable spec artifact and
 * the Markdown spec's §9 table are drift-tested against it.
 */
import { MESSAGES, SCAFFOLD } from "./messages";

import type { MsgKey, MsgParams } from "./messages";
import type { KmdDiagnostic, Severity, Span } from "./types";

export const DIAGNOSTIC_REGISTRY: Record<
  string,
  { severity: Severity; meaning: string }
> = {
  KMD001: {
    severity: "error",
    meaning: "missing/invalid `khiyarsa` version key",
  },
  KMD002: { severity: "error", meaning: "unknown `type` (valid set listed)" },
  KMD003: { severity: "warning", meaning: "unknown frontmatter key ignored" },
  KMD004: { severity: "warning", meaning: "no `type`; defaulted to `flow`" },
  KMD005: {
    severity: "error",
    meaning: "family is spec-valid but not supported by this engine version",
  },
  KMD010: { severity: "error", meaning: "malformed attribute block" },
  KMD011: {
    severity: "error",
    meaning: "unknown attribute bareword (near-matches listed)",
  },
  KMD012: {
    severity: "error",
    meaning: "unknown attribute key / invalid value (valid set listed)",
  },
  KMD013: { severity: "warning", meaning: "out-of-range value clamped" },
  KMD020: {
    severity: "error",
    meaning: "unresolved reference (suggestions; repair: placeholder)",
  },
  KMD021: {
    severity: "error",
    meaning: "ambiguous reference (candidates as dotted paths)",
  },
  KMD022: { severity: "error", meaning: "duplicate explicit id" },
  KMD023: {
    severity: "warning",
    meaning: "duplicate implicit id auto-suffixed",
  },
  KMD024: {
    severity: "error",
    meaning: "unknown participant (sequence messages never create participants)",
  },
  KMD030: {
    severity: "error",
    meaning: "malformed edge statement (expected form shown)",
  },
  KMD031: { severity: "warning", meaning: "non-edge prose paragraph ignored" },
  KMD040: {
    severity: "error",
    meaning: "directive unknown / unclosed / illegally nested",
  },
  KMD041: { severity: "error", meaning: "directive not valid in this family" },
  KMD050: {
    severity: "error",
    meaning: "heading depth not supported by this family",
  },
  KMD051: {
    severity: "warning",
    meaning: "`---` has no meaning in this family",
  },
  KMD060: {
    severity: "error",
    meaning: "document limit exceeded (limit stated)",
  },
  KMD061: {
    severity: "error",
    meaning: "image source violates security policy",
  },
  KMD062: {
    severity: "warning",
    meaning: "remote image deferred (not fetched by this engine version)",
  },
  KMD070: {
    severity: "warning",
    meaning: "icon not found (suggestions listed), rendered without icon",
  },
  KMD071: {
    severity: "warning",
    meaning: "unknown style tag ignored (decorative → warn)",
  },
  KMD080: {
    severity: "info",
    meaning: "section break connections created (listed)",
  },
  KMD081: { severity: "warning", meaning: "section break connected nothing" },
  KMD082: {
    severity: "warning",
    meaning: "section break fanned to >3 nodes (listed)",
  },
  KMD083: {
    severity: "info",
    meaning: "duplicate identical edge collapsed to one",
  },
  KMD090: {
    severity: "info",
    meaning: "normalizer repair applied (what and where)",
  },
  KMD091: {
    severity: "warning",
    meaning: "Mermaid-style syntax detected (KMD equivalent named, no auto-repair)",
  },
  KMD092: {
    severity: "warning",
    meaning: "note lazily swallowed following statement line(s); add a blank line after the note",
  },
  KMD093: {
    severity: "warning",
    meaning: "reference written with declaration-only `#`; resolved by stripping it — write the bare id",
  },
};

export type DiagMessage = string | { key: MsgKey; params?: MsgParams };

export const diag = (
  code: keyof typeof DIAGNOSTIC_REGISTRY & string,
  message: DiagMessage,
  span: Span | null = null,
  extra: Partial<
    Pick<KmdDiagnostic, "found" | "expected" | "expectedAr" | "fix" | "fixAr">
  > = {},
): KmdDiagnostic => ({
  code,
  severity: DIAGNOSTIC_REGISTRY[code]?.severity ?? "error",
  span,
  ...(typeof message === "string"
    ? { message }
    : {
        message: MESSAGES[message.key].en(message.params ?? {}),
        msgKey: message.key,
        params: message.params,
      }),
  ...extra,
});

export type DiagnosticLocale = "en" | "ar";

/** Locale-aware message rendering; falls back to the stored English text. */
export const renderMessage = (
  d: KmdDiagnostic,
  locale: DiagnosticLocale = "en",
): string => {
  const entry = d.msgKey ? MESSAGES[d.msgKey as MsgKey] : undefined;
  const base = entry ? entry[locale](d.params ?? {}) : d.message;
  return d.strictified ? `${base} ${SCAFFOLD[locale].strictMode}` : base;
};

export const renderExpected = (
  d: KmdDiagnostic,
  locale: DiagnosticLocale = "en",
): string | undefined => (locale === "ar" ? d.expectedAr ?? d.expected : d.expected);

export const renderFix = (
  d: KmdDiagnostic,
  locale: DiagnosticLocale = "en",
): string | undefined => (locale === "ar" ? d.fixAr ?? d.fix : d.fix);

export const spanFromMdast = (node: {
  position?: {
    start?: { line?: number; column?: number };
    end?: { line?: number; column?: number };
  };
}): Span | null => {
  const p = node.position;
  if (!p?.start?.line) {
    return null;
  }
  return {
    line: p.start.line,
    col: p.start.column ?? 1,
    endLine: p.end?.line ?? p.start.line,
    endCol: p.end?.column ?? p.start.column ?? 1,
  };
};

/**
 * The stable JSON projection of a diagnostic — the wire shape consumed by AI
 * repair loops (CLI --json, the import dialog's "Copy diagnostics", and the
 * server-side generation endpoint). One definition; do not re-declare.
 */
export const toWireDiagnostic = (d: KmdDiagnostic) => ({
  code: d.code,
  severity: d.severity,
  line: d.span?.line ?? null,
  column: d.span?.col ?? null,
  message: renderMessage(d, "en"),
  key: d.msgKey ?? null,
  found: d.found ?? null,
  expected: d.expected ?? null,
  fix: d.fix ?? null,
});

export type WireDiagnostic = ReturnType<typeof toWireDiagnostic>;

export const formatDiagnostic = (
  d: KmdDiagnostic,
  locale: DiagnosticLocale = "en",
): string => {
  const s = SCAFFOLD[locale];
  const where = d.span ? ` ${d.span.line}:${d.span.col}` : "";
  const parts = [
    `${d.code} ${s.severity[d.severity]}${where} — ${renderMessage(d, locale)}`,
  ];
  const expected = renderExpected(d, locale);
  if (expected) {
    parts.push(`  ${s.expected}: ${expected}`);
  }
  const fix = renderFix(d, locale);
  if (fix) {
    parts.push(`  ${s.fix}: ${fix}`);
  }
  return parts.join("\n");
};

export const hasErrors = (diagnostics: KmdDiagnostic[]): boolean =>
  diagnostics.some((d) => d.severity === "error");
